import PCancelable from 'cancelable-promise'
import PropTypes from 'prop-types'
import React from 'react'
import { Helmet } from 'react-helmet'
import { Redirect } from 'react-router-dom'
import services from '../api/services'
import Button from '../components/Button'
import Field from '../components/Field'
import Header from '../components/Header'
import useNotifications from '../hooks/notifications'
import getReservationSettings from '../settings/reservations'
import { getFormData } from '../utils/data'
import 'date-input-polyfill-react'

const Book = ({
  history: { push },
  match: {
    params: { id: eventId }
  }
}) => {
  const { addNotification } = useNotifications()

  const [event, setEvent] = React.useState()
  const [booking, setBooking] = React.useState(
    JSON.parse(localStorage.getItem('data')) || []
  )
  const [sent, setSent] = React.useState(false)
  const [step, setStep] = React.useState(0)

  const { format } = new Intl.DateTimeFormat('de-DE', {
    month: 'long',
    year: 'numeric'
  })
  const steps = getReservationSettings().fields.filter(
    ({ adminOnly }) => !adminOnly
  )

  const getData = (data, type) => {
    switch (type) {
      case 'checkbox':
        return '✅'
      case 'date':
        return data ? new Date(data).toLocaleDateString() : ''
      case 'month':
        return format(new Date(data))
      default:
        return data
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (step < steps.length) {
      const data = getFormData(e.target)
      setBooking((prevState) => {
        prevState[step] = data
        localStorage.setItem('data', JSON.stringify(prevState))
        return prevState
      })
      setStep((prevState) => prevState + 1)
    } else {
      const bookingRequest = Object.assign(
        { eventId: eventId, eventType: event.type, status: 'Neu' },
        ...booking
      )
      services.reservations.create(bookingRequest).then(({ _id }) => {
        setSent(true)
        addNotification(
          {
            payload: `Bitte klicken Sie nun auf den Bestätigungslink, den wir Ihnen per E-Mail zugesendet haben, damit wir Ihre Buchung weiter bearbeiten können. (Buchungs-Nummer ${_id})`,
            type: 'success'
          },
          false
        )
      }, addNotification)
    }
  }

  React.useEffect(() => {
    const p = new PCancelable((resolve, reject) => {
      services.events.get(eventId).then(resolve, reject)
    })
    p.then(setEvent, addNotification)
    return () => p.cancel()
  }, [addNotification, eventId])

  if (event && event._numReservations >= event.allocation) {
    addNotification({
      payload: 'Diese Veranstaltung ist leider schon ausgebucht!',
      type: 'warning'
    })
    return <Redirect to="/" />
  }

  if (event?.type === 'Reitschule') {
    return <Redirect to="/reitschule" />
  }

  return (
    <div id="app-shell">
      <div className="container mx-auto rounded bg-white px-6 py-4 shadow-xl sm:my-6">
        <Header />

        <Helmet>
          <title>{window.document.head.dataset.titlePrefix} | Start</title>
        </Helmet>

        <h1 className="mb-4 text-2xl">Veranstaltung buchen</h1>

        {event ? (
          <>
            <h2 className="mb-2 text-3xl leading-tight">
              {event.title} ({event.type})
            </h2>

            <p className="mb-6">
              <strong>
                {new Date(event.dateFrom).toLocaleDateString()} -{' '}
                {new Date(event.dateTo).toLocaleDateString()}
              </strong>
              {/*
              <span className='bg-yellow-500 ml-2 px-1 px-2 py-1 rounded text-sm'>
                Noch {event.allocation - event._numReservations} Plätze frei
              </span>
              */}
            </p>

            <form onSubmit={handleSubmit}>
              <div className="-mx-3 -mt-1 flex-wrap sm:flex">
                <fieldset
                  className="mx-3 mb-8 w-full rounded border px-6 py-2 shadow"
                  style={{ minWidth: 0 }}
                >
                  <legend className="-mx-2 bg-white px-2 text-blue-700 uppercase">
                    {steps[step]?.legend || 'Buchungsanfrage absenden'} (
                    {step + 1} / {steps.length + 1})
                  </legend>
                  <div className="-mx-6 flex-wrap px-3 sm:flex">
                    {steps[step] ? (
                      steps[step].fieldset
                        .filter(({ hideEditor }) => !hideEditor)
                        .map(
                          ({
                            name,
                            options,
                            parse,
                            tableHeader,
                            wrap,
                            ...props
                          }) => (
                            <div
                              className="my-3 w-full px-3 md:w-1/2"
                              key={name}
                            >
                              <Field
                                name={name}
                                options={
                                  Array.isArray(options) ? options : undefined
                                }
                                {...props}
                                defaultChecked={
                                  booking[step] &&
                                  (props.type === 'checkbox'
                                    ? booking[step][name]
                                    : undefined)
                                }
                                defaultValue={
                                  booking[step] &&
                                  (props.type === 'checkbox'
                                    ? undefined
                                    : booking[step][name])
                                }
                              />
                            </div>
                          )
                        )
                    ) : (
                      <div className="w-full px-3 text-sm">
                        <p className="italic">Bitte prüfen Sie Ihre Daten:</p>
                        <div className="overflow-x-auto" style={{ padding: 1 }}>
                          <table className="mb-3 w-full table-auto">
                            {booking.map((data, step) => (
                              <React.Fragment key={steps[step].legend}>
                                <thead>
                                  <tr>
                                    <th
                                      className="px-4 pt-4 pb-2 text-left"
                                      colSpan="2"
                                    >
                                      {steps[step].legend}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.keys(data).map((key, i) => (
                                    <tr key={key}>
                                      <td
                                        className="border px-4 py-2 md:w-1/3"
                                        // biome-ignore lint/security/noDangerouslySetInnerHtml: intentional
                                        dangerouslySetInnerHTML={{
                                          __html: `${steps[step].fieldset[i].label}:`
                                        }}
                                      />
                                      <td className="border px-4 py-2">
                                        {getData(
                                          data[key],
                                          steps[step].fieldset[i].type
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </React.Fragment>
                            ))}
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
              </div>

              {step === steps.length && (
                <div className="rounded border-2 border-green-500 p-3 shadow">
                  <span className="font-bold text-green-500 uppercase">
                    Bitte beachten
                  </span>
                  : Nach dem Absenden der Buchungsanfrage erhalten Sie eine
                  E-Mail mit einem Bestätigungslink. Bitte klicken Sie auf den
                  Link, um Ihre E-Mail-Adresse zu bestätigen. Erst nach dieser
                  Bestätigung können wir Ihre Buchung weiter bearbeiten.
                </div>
              )}

              <div
                className="sticky bottom-0 -mx-6 mt-3 flex justify-between px-3 py-4"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)' }}
              >
                <div className="mx-3">
                  <Button
                    onClick={() =>
                      step > 0
                        ? setStep((prevState) =>
                            prevState > 1 ? prevState - 1 : 0
                          )
                        : push('/')
                    }
                    type="button"
                  >
                    Zurück
                  </Button>{' '}
                </div>
                <div className="mx-3">
                  <Button
                    color={sent ? 'success' : 'primary'}
                    disabled={sent}
                    type="submit"
                  >
                    {sent
                      ? 'Buchungsanfrage versendet'
                      : step === steps.length
                        ? 'Buchungsanfrage absenden'
                        : 'Weiter'}
                  </Button>
                </div>
              </div>
            </form>
          </>
        ) : (
          <p>Lade…</p>
        )}
      </div>
    </div>
  )
}

Book.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
}

export default Book
